import type { Video } from '../../api/videos/types'
import type { Document } from '../../api/documents/types'
import type { CompanyBrief } from '../../api/companies/types'
import { VideoFilter } from '../../ui/containers/content/videos/types'
import { DocumentFilter } from '../../ui/containers/content/documents/types'

export const SORTING_OPTIONS = {
  DEFAULT: '',
  RECOMMENDED: 'recommended',
  LATEST: 'by-date',
  ALPHABETIC: 'alphabetic',
}

export function sortContent<T extends Video | Document | CompanyBrief>  (content: Array<T> | undefined, sortBy?: string, sortProp?: keyof T, ): Array<T> | null {
  if(!content) return null
  if(!sortBy || sortBy === '' || sortBy === SORTING_OPTIONS.RECOMMENDED) {
    content.sort((a, b) => (a.sortPosition || 0) - (b.sortPosition || 0))
    return content
  }
  if(sortBy === SORTING_OPTIONS.LATEST) {
    content.sort((a, b) => new Date((b as Video | Document).createdAt).getTime() - new Date((a as Video | Document).createdAt).getTime())
    return content
  }
  if(sortBy === SORTING_OPTIONS.ALPHABETIC && sortProp) {
    content.sort((a, b) => (a[sortProp] as string).localeCompare(b[sortProp] as string))
    return content
  }
  return content
}

type Content = Video | Document
type Filter = VideoFilter | DocumentFilter

export const filterContent = <T extends Content>(content: T[] | undefined, filter: Partial<Filter>): T[] => {
  if (!content) {
    return []
  }

  return content.filter(item => {
    // Filter by categories
    if (filter.categories && filter.categories.length > 0 && !item.categories.some(cat => filter.categories?.includes(cat.id.toString()))) {
      return false
    }

    // Filter by difficulty levels
    if (filter.difficultyLevels && filter.difficultyLevels.length > 0 && !filter.difficultyLevels?.includes(item.difficultyLevel.id.toString())) {
      return false
    }

    // Filter by main category
    if (filter.mainCategory && item.mainCategory.id?.toString() !== filter.mainCategory?.toString()) {
      return false
    }

    // Filter by text (search in title and authors)
    if (filter.text) {
      const searchText = filter.text.toLowerCase()
      if (!item.title.toLowerCase().includes(searchText) &&
          !item.authors.some(author => author.toLowerCase().includes(searchText))) {
        return false
      }
    }

    // Filter by video types (for videos only)
    if ('videoTypes' in filter && 'type' in item) {
      if (filter.videoTypes && filter.videoTypes.length > 0 && !filter.videoTypes.includes(item.type.id?.toString())) {
        return false
      }
    }

    // Filter by document types (for documents only)
    if ('docTypes' in filter && 'type' in item) {
      if (filter.docTypes && filter.docTypes.length > 0 && !filter.docTypes.includes(item.type.id?.toString())) {
        return false
      }
    }

    return true
  })
}
